/**
 * 모임 기록 — 목록과 요약.
 *
 * 장소별 후기(usePlaceReviews)와 같은 테이블을 보지만 묶는 기준이 다릅니다.
 * 저쪽은 장소 하나, 이쪽은 모임 하나입니다.
 *
 * 요약을 따로 둔 이유
 *   메인탭 오늘 카드는 사진 넉 장과 개수만 그립니다. 거기서 목록 스무 건을
 *   받아 두 줄만 쓰는 건 낭비라 서버에 요약 전용 경로를 뒀습니다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { unwrapList, unwrapData } from 'common/utils/apiResponse';

/** 서버가 주는 기록 한 건 */
export interface JournalEntry {
    reviewId: number;
    userNo: number;
    nickname?: string | null;
    placeId: string;
    placeName?: string | null;
    dayNumber?: number | null;
    subId?: number | null;
    rating?: number | null;
    content?: string | null;
    likeCount?: number | null;
    commentCount?: number | null;
    liked?: boolean | null;
    mine?: boolean | null;
    createdAt?: string | null;
    photos?: { photoId: number; imageUrl: string }[] | null;
}

export interface JournalSummary {
    entryCount: number;
    photoCount: number;
    photos: string[];
    latest?: {
        reviewId: number;
        nickname?: string | null;
        placeName?: string | null;
        content?: string | null;
        createdAt?: string | null;
    } | null;
}

const PAGE_SIZE = 20;

export const useMeetingJournal = (meetingId?: string | null, sort: 'time' | 'day' = 'time') => {
    const [entries, setEntries] = useState<JournalEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(1);

    const load = useCallback(async (nextPage: number) => {
        if (!meetingId) return;
        setLoading(true);
        try {
            const res = await PlaceReviewRepository.getMeetingJournal(meetingId, {
                sort, page: nextPage, size: PAGE_SIZE,
            });
            const list = unwrapList<JournalEntry>(res);
            setEntries(prev => (nextPage === 1 ? list : [...prev, ...list]));
            setHasMore(list.length >= PAGE_SIZE);
            setPage(nextPage);
        } finally {
            setLoading(false);
        }
    }, [meetingId, sort]);

    /* 정렬이 바뀌면 처음부터 다시 받습니다 */
    useEffect(() => {
        let cancelled = false;
        if (!meetingId) return undefined;
        (async () => {
            if (cancelled) return;
            await load(1);
        })();
        return () => { cancelled = true; };
    }, [meetingId, sort, load]);

    const reload = useCallback(() => load(1), [load]);
    const loadMore = useCallback(() => {
        if (!loading && hasMore) load(page + 1);
    }, [loading, hasMore, page, load]);

    /* 일차별 보기에서 쓰는 묶음. 일차가 없는 기록은 맨 뒤에 모읍니다 */
    const byDay = useMemo(() => {
        const groups = new Map<number | null, JournalEntry[]>();
        for (const e of entries) {
            const key = e.dayNumber ?? null;
            const list = groups.get(key);
            if (list) list.push(e); else groups.set(key, [e]);
        }
        return Array.from(groups.entries())
            .sort((a, b) => {
                if (a[0] === null) return 1;
                if (b[0] === null) return -1;
                return b[0] - a[0];   // 최근 일차가 위 — 여행 중에는 오늘이 먼저 궁금합니다
            })
            .map(([day, items]) => ({ day, items }));
    }, [entries]);

    return { entries, byDay, loading, hasMore, reload, loadMore };
};

/**
 * 기록 요약 — 사진 몇 장 + 개수 + 최근 한 건.
 *
 * 메인탭 오늘 카드는 이 훅을 쓰지 않습니다. 거기 카드(TodayHero)는 Home 안에서
 * 정의된 컴포넌트라 Home 이 렌더될 때마다 통째로 다시 마운트되고, useEffect 로
 * 부르는 훅을 두면 렌더마다 다시 부르게 됩니다. 그래서 Home 은 직접 받아 두고
 * 값만 내려 줍니다.
 *
 * 모임 상세는 사정이 다릅니다 — 화면 자체가 컴포넌트라 다시 마운트되지 않습니다.
 */
export const useJournalSummary = (meetingId?: string | null) => {
    const [summary, setSummary] = useState<JournalSummary | null>(null);

    const reload = useCallback(async () => {
        if (!meetingId) { setSummary(null); return; }
        const res = await PlaceReviewRepository.getMeetingJournalSummary(meetingId);
        /* 요약이 없다고 화면이 깨지면 안 됩니다 — 값만 비웁니다 */
        setSummary(unwrapData<JournalSummary>(res) || null);
    }, [meetingId]);

    useEffect(() => { void reload(); }, [reload]);

    return { summary, reload };
};
